// File: src/InsuranceQuestionnaire.js
// Insurance questionnaire step: date passed test + six yes/no declarations.
//
// ─────────────────────────────────────────────────────────────────────────────
// v3.13: HOISTED OUT OF App.js — do not move it back inside another component.
//
// It used to be declared inside the App component body. A component declared
// inside another component is recreated as a NEW function object on every
// render of its parent, and React decides whether to keep a subtree by
// comparing component IDENTITY. Seeing a stranger, it unmounted the old
// questionnaire and mounted a fresh one — taking `formData` with it — then the
// new instance's mount effect refilled the form from `driverStatus.insuranceData`,
// i.e. the answers as they were when the page first loaded.
//
// The visible symptom: clicking Continue calls setLoading(true) in App, App
// re-renders, the form is demolished and rebuilt, and the driver watches their
// answers flash back to the previous ones. It still SAVED correctly, because
// the submitted object was passed by value into the handler before the rebuild.
//
// The invisible symptom was worse: ANY App-level state change mid-form silently
// reverted in-progress answers to the stale saved ones. Only setLoading and the
// session-timeout timer could do it, but the next state update added to App
// would have got a data-loss bug for free.
//
// YesNoQuestion is declared at module scope here for the same reason — nested
// inside the questionnaire, it remounted all six questions on every single
// click. That churn is what the old preserveScrollPosition/restoreScrollPosition
// calls existed to paper over: they snapshotted window.scrollY on every answer
// and forced a window.scrollTo back to it on a 0ms timeout.
//
// v3.14: removed, verified on a real device. With no remount there is nothing to
// paper over — answering a question only ever changes layout BELOW the tap (the
// "details required" hint, the date's validity line), so nothing above it moves.
// The hack was also actively harmful: a forced scrollTo one tick after every tap
// yanks a driver back if they start scrolling straight after answering.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import { StepShell, Card, PrimaryButton, YesNo, Note, PHASE } from './ui/HireFormUI';

// Module scope on purpose — see the note at the top of this file.
// Both answers look the same (no red "yes"); No comes first.
const YesNoQuestion = ({ field, question, hint, value, onChange, error }) => {
  return (
    <div className="hf-question">
      <p className="hf-question-text">{question}</p>
      {hint && <p className="hf-help-text">{hint}</p>}
      <YesNo value={value} onChange={(v) => onChange(field, v)} name={question} />
      {error && <p className="hf-error-text">{error}</p>}
    </div>
  );
};

const InsuranceQuestionnaire = ({
  driverStatus,
  driverEmail,
  jobId,
  loading,
  onComplete,
  onBack,
}) => {
  const [formData, setFormData] = useState({
    datePassedTest: '',
    hasDisability: null,
    hasConvictions: null,
    hasProsecution: null,
    hasAccidents: null,
    hasInsuranceIssues: null,
    hasDrivingBan: null,
    // Oct 2026 (OP migration 298): declarations that don't refer on their own.
    hasRecentConvictions: null,
    hasFaultClaims: null,
    additionalDetails: ''
  });
  
  const [errors, setErrors] = useState({});
  const [datePassedTestError, setDatePassedTestError] = useState('');
  const isReturningDriver = driverStatus?.status !== 'new';

  // Pre-populate from insuranceData if available.
  //
  // The questions were reworded in Oct 2026 to follow the insurer's guide, and
  // some now ask about more (accidents: 3 → 5 years and claims; bans: plus
  // suspensions). An old "no" carried over under the new wording could be
  // wrong, so the answers are only pre-filled when they were given to THIS set
  // of questions — which is when hasRecentConvictions is a real answer rather
  // than null. Otherwise a returning driver answers afresh (date kept).
  useEffect(() => {
    const ins = driverStatus?.insuranceData;
    if (ins) {
      const answeredCurrentSet = typeof ins.hasRecentConvictions === 'boolean';
      const yn = (v) => (v === true ? 'yes' : v === false ? 'no' : null);
      setFormData({
        datePassedTest: ins.datePassedTest || '',
        hasDisability: answeredCurrentSet ? yn(ins.hasDisability) : null,
        hasConvictions: answeredCurrentSet ? yn(ins.hasConvictions) : null,
        hasProsecution: answeredCurrentSet ? yn(ins.hasProsecution) : null,
        hasAccidents: answeredCurrentSet ? yn(ins.hasAccidents) : null,
        hasInsuranceIssues: answeredCurrentSet ? yn(ins.hasInsuranceIssues) : null,
        hasDrivingBan: answeredCurrentSet ? yn(ins.hasDrivingBan) : null,
        hasRecentConvictions: answeredCurrentSet ? yn(ins.hasRecentConvictions) : null,
        hasFaultClaims: answeredCurrentSet && ins.hasAccidents ? yn(ins.hasFaultClaims) : null,
        additionalDetails: answeredCurrentSet ? (ins.additionalDetails || '') : ''
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateDatePassedTest = (date) => {
    if (!date) {
      setDatePassedTestError('Please enter the date you passed your driving test');
      return false;
    }

    const passedDate = new Date(date);
    const today = new Date();
    
    if (isNaN(passedDate.getTime())) {
      setDatePassedTestError('Please enter a valid date');
      return false;
    }

    if (passedDate > today) {
      setDatePassedTestError('Date cannot be in the future');
      return false;
    }

    const yearsDifference = (today - passedDate) / (1000 * 60 * 60 * 24 * 365.25);
    
    if (yearsDifference < 2) {
      setDatePassedTestError(`You must have held your licence for at least 2 years. Currently: ${yearsDifference.toFixed(1)} years`);
      return false;
    }

    if (yearsDifference > 70) {
      setDatePassedTestError('Please check the date entered');
      return false;
    }

    setDatePassedTestError('');
    return true;
  };

  const handleQuestionChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // The follow-up only exists when there was an accident or claim.
      ...(field === 'hasAccidents' && value !== 'yes' ? { hasFaultClaims: null } : {})
    }));
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Details are asked for ANY yes — including the two declarations that don't
  // refer on their own, because the insurer's guide wants convictions and
  // claims from the last 5 years on record (what, when, points).
  const hasYesAnswers = () => {
    return ['hasDisability', 'hasRecentConvictions', 'hasConvictions', 'hasProsecution', 'hasAccidents',
      'hasFaultClaims', 'hasInsuranceIssues', 'hasDrivingBan']
      .some(field => formData[field] === 'yes');
  };

  const validateQuestions = () => {
    const newErrors = {};
    
    if (!validateDatePassedTest(formData.datePassedTest)) {
      newErrors.datePassedTest = datePassedTestError;
    }
    
    const requiredFields = [
      'hasDisability', 'hasRecentConvictions', 'hasConvictions', 'hasProsecution',
      'hasAccidents', 'hasInsuranceIssues', 'hasDrivingBan',
      // Only asked when there was an accident or claim.
      ...(formData.hasAccidents === 'yes' ? ['hasFaultClaims'] : [])
    ];
    
    requiredFields.forEach(field => {
      if (formData[field] === null) {
        newErrors[field] = 'Please select an option';
      }
    });

    if (hasYesAnswers() && !formData.additionalDetails.trim()) {
      newErrors.additionalDetails = 'Please provide additional details for your "Yes" answers';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateQuestions()) return;

    try {
      const submissionData = {
        ...formData,
        email: driverEmail,
        jobId: jobId,
        submittedAt: new Date().toISOString()
      };

      await onComplete(submissionData);
      
    } catch (error) {
      console.error('❌ Submission error:', error.message);
      // App.handleInsuranceComplete throws a driver-facing message when the
      // answers could not be saved — show it rather than a generic line.
      setErrors({ submit: (error && error.message) || 'Failed to submit questionnaire. Please try again.' });
    }
  };

  const answeredCurrentSet = typeof driverStatus?.insuranceData?.hasRecentConvictions === 'boolean';
  const yearsHeld = formData.datePassedTest
    ? ((new Date() - new Date(formData.datePassedTest)) / (1000 * 60 * 60 * 24 * 365.25)).toFixed(1)
    : null;
  const showDetails = hasYesAnswers() || !!formData.additionalDetails;

  return (
    <StepShell jobId={jobId} phase={PHASE.YOU}>
      {isReturningDriver && (
        answeredCurrentSet ? (
          <Note tone="accent">
            <strong>We've filled in your answers from last time.</strong> Please read each one and
            change anything that's no longer true.
          </Note>
        ) : (
          <Note tone="accent">
            <strong>Our questions have changed,</strong> so please answer them again.
          </Note>
        )
      )}

      <Card>
        <div>
          <h2 className="hf-h2">Insurance questions</h2>
          <p className="hf-intro" style={{ marginTop: 6 }}>
            Our insurer asks every driver these questions. Answering yes doesn't automatically stop you
            driving, but our insurer may need to look at it first. They may add a charge or a higher
            excess, or say no. Not sure about one? Answer yes and explain below.
          </p>
        </div>

        <div className="hf-field" style={{ borderTop: '1px solid var(--hf-divider)', paddingTop: 16 }}>
          <label className="hf-label" htmlFor="datePassedTest">When did you pass your driving test?</label>
          <p className="hf-help-text">You need to have held a full car licence (category B) for at least 2 years.</p>
          <input
            type="date"
            id="datePassedTest"
            name="datePassedTest"
            value={formData.datePassedTest}
            onChange={(e) => {
              handleQuestionChange('datePassedTest', e.target.value);
              validateDatePassedTest(e.target.value);
            }}
            onBlur={() => validateDatePassedTest(formData.datePassedTest)}
            max={new Date().toISOString().split('T')[0]}
            className={`hf-input${datePassedTestError ? ' is-error' : ''}`}
            required
          />
          {datePassedTestError && <p className="hf-error-text">{datePassedTestError}</p>}
          {!datePassedTestError && yearsHeld && <p className="hf-ok-text">✓ Held for {yearsHeld} years</p>}
        </div>
      </Card>

      {/* Insurance Questions — Oct 2026, checked against the Markerstudy
          referencing guide (Feb 2026). Each topic the guide wants RECORDED
          but doesn't refer on (any conviction / any accident or claim in 5
          years) is its own question, separate from the one that does refer,
          so a 3-point speeding or a single claim isn't a needless referral.
          Which answers raise a referral: App.handleInsuranceComplete. */}
      <Card>
          <YesNoQuestion
            field="hasDisability"
            question="Has the DVLA (or DVA), a doctor or your GP told you not to drive, or put any restrictions on your driving, even temporarily?"
            hint="Having a medical condition or disability is fine on its own. Needing glasses or contact lenses to drive doesn't count."
            value={formData.hasDisability}
            onChange={handleQuestionChange}
            error={errors.hasDisability}
          />

          <YesNoQuestion
            field="hasRecentConvictions"
            question="In the last 5 years, have you been convicted of any motoring offence, including speeding?"
            hint="Most minor offences are fine. Tell us what, when and how many points below."
            value={formData.hasRecentConvictions}
            onChange={handleQuestionChange}
            error={errors.hasRecentConvictions}
          />

          <YesNoQuestion
            field="hasConvictions"
            question="Have you ever been convicted of dangerous driving, driving while disqualified, or taking a vehicle without consent? Or, in the last 5 years, of a single offence that carried 6 or more points (not counting speeding or using a phone)?"
            hint="On a UK licence these are DD, BA or UT codes, or a single 6+ point offence such as MS90 (not naming a driver) or IN10 (no insurance)."
            value={formData.hasConvictions}
            onChange={handleQuestionChange}
            error={errors.hasConvictions}
          />

          <YesNoQuestion
            field="hasProsecution"
            question="Is a prosecution pending against you for any motoring offence?"
            value={formData.hasProsecution}
            onChange={handleQuestionChange}
            error={errors.hasProsecution}
          />

          <YesNoQuestion
            field="hasAccidents"
            question="In the last 5 years, have you had any motor accidents or made any motor insurance claims, whether or not they were your fault?"
            value={formData.hasAccidents}
            onChange={handleQuestionChange}
            error={errors.hasAccidents}
          />

          {formData.hasAccidents === 'yes' && (
            <YesNoQuestion
              field="hasFaultClaims"
              question="Have more than one of them been your fault, or still open (not yet settled)?"
              value={formData.hasFaultClaims}
              onChange={handleQuestionChange}
              error={errors.hasFaultClaims}
            />
          )}

          <YesNoQuestion
            field="hasInsuranceIssues"
            question="Have you ever been refused motor insurance, or had special terms (such as a higher excess) put on it?"
            value={formData.hasInsuranceIssues}
            onChange={handleQuestionChange}
            error={errors.hasInsuranceIssues}
          />

          <YesNoQuestion
            field="hasDrivingBan"
            question="In the last 5 years, have you been banned or disqualified from driving, or had your licence suspended?"
            value={formData.hasDrivingBan}
            onChange={handleQuestionChange}
            error={errors.hasDrivingBan}
          />
      </Card>

      {showDetails && (
        <Card>
          <div className="hf-field">
            <label className="hf-label" htmlFor="additionalDetails">
              {hasYesAnswers() ? 'Tell us a bit more' : 'Anything else to tell us? (optional)'}
            </label>
            {hasYesAnswers() && (
              <p className="hf-help-text">
                For each yes: what happened, when, and any points. We'll pass this to our insurer and
                get back to you if they need anything.
              </p>
            )}
            <textarea
              id="additionalDetails"
              value={formData.additionalDetails}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, additionalDetails: e.target.value }));
                if (errors.additionalDetails) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.additionalDetails;
                    return newErrors;
                  });
                }
              }}
              rows={4}
              className={`hf-textarea${errors.additionalDetails ? ' is-error' : ''}`}
            />
            {errors.additionalDetails && <p className="hf-error-text">{errors.additionalDetails}</p>}
          </div>
        </Card>
      )}

      <p className="hf-small">
        Please answer honestly. Giving false information will invalidate your insurance.
      </p>

      {errors.submit && <Note tone="error">{errors.submit}</Note>}

      <PrimaryButton onClick={handleSubmit} busy={loading} busyLabel="Saving…">
        Save and continue
      </PrimaryButton>
      <button type="button" className="hf-link-btn" onClick={onBack} style={{ alignSelf: 'center' }}>
        Back to your phone number
      </button>
    </StepShell>
  );
};

export default InsuranceQuestionnaire;
